import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";

const ADMIN_KEY = "aabb_admin_token";

function adminHeaders() {
  const t = localStorage.getItem(ADMIN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function StatCard({ label, value, icon, trend, testId }) {
  return (
    <div data-testid={testId} style={{
      background:"var(--dark)", border:"1px solid rgba(193,127,58,.22)", borderRadius:14,
      padding:"24px", flex:1, minWidth:"220px"
    }}>
      <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:12}}>
        <div className="aa-eyebrow" style={{marginBottom:0}}>{label}</div>
        {icon && <div style={{fontSize:"1.5rem"}}>{icon}</div>}
      </div>
      <div style={{fontFamily:"var(--font-display)", color:"var(--gold)", fontSize:"2.4rem", fontWeight:700, marginBottom:4}}>
        {value ?? "—"}
      </div>
      {trend && <div style={{fontSize:".75rem", color:"#7AB871"}}>{trend}</div>}
    </div>
  );
}

function TabButton({ active, onClick, label, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      style={{
        background: active ? "var(--gold)" : "transparent",
        color: active ? "var(--black)" : "var(--gold)",
        border: active ? "none" : "1px solid var(--gold)",
        padding:"10px 20px",
        letterSpacing:".10em",
        textTransform:"uppercase",
        fontSize:".82rem",
        fontWeight:700,
        cursor:"pointer",
        borderRadius:6,
        transition:"all .2s"
      }}
    >
      {label}
    </button>
  );
}

export default function AdminDashboard() {
  const nav = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState({ rows:[], total:0 });
  const [orders, setOrders] = useState({ rows:[], total:0 });
  const [page, setPage] = useState(1);
  const [orderPage, setOrderPage] = useState(1);
  const [pageSize] = useState(25);
  const [logs, setLogs] = useState([]);
  const [series, setSeries] = useState([]);
  const [err, setErr] = useState("");

  // Analytics codes state
  const [analyticsConfig, setAnalyticsConfig] = useState({
    google_analytics: "",
    facebook_pixel: "",
    tiktok_pixel: "",
    custom_header: "",
    custom_footer: ""
  });
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");

  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem(ADMIN_KEY)) {
      nav("/admin/login");
    } else {
      setAuthChecked(true);
    }
  }, [nav]);

  const handle401 = (e) => {
    if (e?.response?.status === 401) {
      console.error("Admin API returned 401:", e?.response?.data);
      // Token expired or invalid - redirect to login
      setErr("Session expired. Please log in again.");
      setTimeout(() => {
        localStorage.removeItem(ADMIN_KEY);
        nav("/admin/login");
      }, 2000);
      return true;
    }
    return false;
  };

  const loadOverview = async () => {
    try {
      setErr("");
      const [s, p] = await Promise.all([
        api.get("/admin/stats", { headers: adminHeaders() }),
        api.get("/admin/pageviews-daily?days=30", { headers: adminHeaders() }),
      ]);
      setStats(s.data);
      setSeries(p.data.series || []);
    } catch (e) {
      if (handle401(e)) return;
      setErr(e?.response?.data?.detail || "Failed to load stats");
    }
  };

  const loadLeads = async () => {
    try {
      const l = await api.get(`/admin/leads?page=${page}&page_size=${pageSize}`, { headers: adminHeaders() });
      setLeads(l.data);
    } catch (e) {
      if (handle401(e)) return;
    }
  };

  const loadOrders = async () => {
    try {
      const o = await api.get(`/admin/orders?page=${orderPage}&page_size=${pageSize}`, { headers: adminHeaders() });
      setOrders(o.data);
    } catch (e) {
      if (handle401(e)) return;
    }
  };

  const loadWebhookLogs = async () => {
    try {
      const w = await api.get("/admin/webhook-logs?limit=50", { headers: adminHeaders() });
      setLogs(w.data.rows || []);
    } catch (e) {
      if (handle401(e)) return;
    }
  };

  const loadAnalytics = async () => {
    try {
      const a = await api.get("/admin/analytics-config", { headers: adminHeaders() });
      if (a.data) setAnalyticsConfig(a.data);
    } catch (e) {
      if (handle401(e)) return;
    }
  };

  useEffect(() => {
    if (activeTab === "overview") loadOverview();
    else if (activeTab === "leads") loadLeads();
    else if (activeTab === "orders") loadOrders();
    else if (activeTab === "webhooks") loadWebhookLogs();
    else if (activeTab === "analytics") loadAnalytics();
  }, [activeTab, page, orderPage]); // eslint-disable-line

  const saveAnalytics = async () => {
    setSaving(true);
    setSaveMessage("");
    try {
      await api.post("/admin/analytics-config", analyticsConfig, { headers: adminHeaders() });
      setSaveMessage("Saved successfully! Changes will appear on the website immediately.");
      setTimeout(() => setSaveMessage(""), 5000);
    } catch (e) {
      if (handle401(e)) return;
      setSaveMessage("Failed to save");
    }
    setSaving(false);
  };

  const signOut = () => { localStorage.removeItem(ADMIN_KEY); nav("/admin/login"); };

  const maxCount = useMemo(() => Math.max(1, ...series.map(s=>s.count)), [series]);

  // Don't render until auth is checked
  if (!authChecked) return null;

  return (
    <div style={{minHeight:"100vh", background:"var(--black)", color:"var(--white)", padding:"32px 4vw 80px"}}>
      {/* Header */}
      <header style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:32}}>
        <div>
          <div className="aa-eyebrow">Internal Dashboard</div>
          <h1 className="aa-h1" style={{marginBottom:0}}>Admin Console</h1>
        </div>
        <button onClick={signOut} data-testid="admin-signout" style={{
          background:"transparent", border:"1px solid var(--gold)", color:"var(--gold)",
          padding:"12px 24px", letterSpacing:".12em", textTransform:"uppercase", fontSize:".82rem", cursor:"pointer", borderRadius:6
        }}>Sign Out</button>
      </header>

      {err && <div className="aa-error" style={{marginBottom:20}}>{err}</div>}

      {/* Tabs */}
      <div style={{display:"flex", gap:12, marginBottom:32, flexWrap:"wrap"}}>
        <TabButton active={activeTab==="overview"} onClick={()=>setActiveTab("overview")} label="Overview" testId="tab-overview"/>
        <TabButton active={activeTab==="leads"} onClick={()=>setActiveTab("leads")} label="Leads" testId="tab-leads"/>
        <TabButton active={activeTab==="orders"} onClick={()=>setActiveTab("orders")} label="Orders" testId="tab-orders"/>
        <TabButton active={activeTab==="webhooks"} onClick={()=>setActiveTab("webhooks")} label="Webhooks" testId="tab-webhooks"/>
        <TabButton active={activeTab==="analytics"} onClick={()=>setActiveTab("analytics")} label="Analytics & Tracking" testId="tab-analytics"/>
      </div>

      {/* Overview Tab */}
      {activeTab === "overview" && (
        <div>
          {/* Stats Cards */}
          <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(220px, 1fr))", gap:16, marginBottom:32}}>
            <StatCard label="Total Submissions" value={stats?.total_submissions} testId="stat-total"/>
            <StatCard label="Today" value={stats?.submissions_today} testId="stat-today"/>
            <StatCard label="This Week" value={stats?.submissions_week} testId="stat-week"/>
            <StatCard label="This Month" value={stats?.submissions_month} testId="stat-month"/>
            <StatCard label="Total Orders" value={stats?.total_orders || 0} testId="stat-orders"/>
            <StatCard label="Revenue (MTD)" value={stats?.revenue_month ? `$${(stats.revenue_month/100).toLocaleString()}` : "$0"} testId="stat-revenue"/>
          </div>

          {/* Page Views Chart */}
          <div style={{background:"var(--dark)", border:"1px solid rgba(193,127,58,.22)", borderRadius:14, padding:"24px", marginBottom:32}}>
            <h3 className="aa-eyebrow" style={{marginBottom:20}}>Page Views — Last 30 Days</h3>
            <div style={{display:"flex", alignItems:"flex-end", gap:6, height:180, padding:"0 10px"}}>
              {series.length === 0 && <div style={{color:"var(--muted)"}}>No data yet</div>}
              {series.map(d => {
                const barHeight = (d.count/maxCount)*150;
                return (
                  <div key={d.date} title={`${d.date}: ${d.count} views`} style={{
                    flex:1,
                    display:"flex",
                    flexDirection:"column",
                    alignItems:"center",
                    gap:8
                  }}>
                    <div style={{fontSize:".7rem", color:"var(--gold)", fontWeight:700}}>{d.count}</div>
                    <div style={{
                      width:"100%",
                      height:`${barHeight}px`,
                      background:"linear-gradient(180deg, var(--gold), var(--gold-dark))",
                      borderRadius:"4px 4px 0 0",
                      minHeight:4,
                      transition:"all .2s",
                      cursor:"pointer"
                    }}
                    onMouseEnter={(e)=>{e.currentTarget.style.opacity="0.8"}}
                    onMouseLeave={(e)=>{e.currentTarget.style.opacity="1"}}
                    />
                    <div style={{fontSize:".65rem", color:"var(--muted)", whiteSpace:"nowrap"}}>{d.date.slice(5)}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Quick Stats */}
          <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(280px, 1fr))", gap:16}}>
            <div style={{background:"var(--dark)", border:"1px solid rgba(193,127,58,.22)", borderRadius:14, padding:"24px"}}>
              <h3 className="aa-eyebrow" style={{marginBottom:12}}>Top Program</h3>
              <div style={{fontSize:"1.3rem", color:"var(--gold)", fontWeight:700}}>{stats?.top_program || "—"}</div>
            </div>
            <div style={{background:"var(--dark)", border:"1px solid rgba(193,127,58,.22)", borderRadius:14, padding:"24px"}}>
              <h3 className="aa-eyebrow" style={{marginBottom:12}}>Conversion Rate</h3>
              <div style={{fontSize:"1.3rem", color:"var(--gold)", fontWeight:700}}>
                {stats?.conversion_rate ? `${stats.conversion_rate}%` : "0%"}
              </div>
            </div>
            <div style={{background:"var(--dark)", border:"1px solid rgba(193,127,58,.22)", borderRadius:14, padding:"24px"}}>
              <h3 className="aa-eyebrow" style={{marginBottom:12}}>Avg Order Value</h3>
              <div style={{fontSize:"1.3rem", color:"var(--gold)", fontWeight:700}}>
                {stats?.avg_order_value ? `$${(stats.avg_order_value/100).toFixed(2)}` : "$0.00"}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Leads Tab */}
      {activeTab === "leads" && (
        <LeadsTable
          leads={leads}
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          adminHeaders={adminHeaders}
          reload={loadLeads}
          handle401={handle401}
        />
      )}

      {/* Orders Tab */}
      {activeTab === "orders" && (
        <OrdersTable
          orders={orders}
          page={orderPage}
          setPage={setOrderPage}
          pageSize={pageSize}
        />
      )}

      {/* Webhooks Tab */}
      {activeTab === "webhooks" && (
        <WebhooksTable logs={logs} />
      )}

      {/* Analytics Tab */}
      {activeTab === "analytics" && (
        <AnalyticsConfig
          config={analyticsConfig}
          setConfig={setAnalyticsConfig}
          onSave={saveAnalytics}
          saving={saving}
          saveMessage={saveMessage}
        />
      )}
    </div>
  );
}

// Leads Table Component
function LeadsTable({ leads, page, setPage, pageSize, adminHeaders, reload, handle401 }) {
  const [resending, setResending] = useState(null);
  const totalPages = Math.max(1, Math.ceil(leads.total / pageSize));

  const resend = async (id) => {
    setResending(id);
    try {
      await api.post(`/admin/resend-webhook/${id}`, {}, { headers: adminHeaders() });
      await reload();
    } catch (e) { handle401(e); }
    setResending(null);
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/leads/${id}/status`, { status }, { headers: adminHeaders() });
      reload();
    } catch (e) { handle401(e); }
  };

  return (
    <div>
      <h2 className="aa-h2" style={{fontSize:"1.4rem", marginBottom:20}}>Leads ({leads.total})</h2>
      <div style={{background:"var(--dark)", border:"1px solid rgba(193,127,58,.22)", borderRadius:14, overflow:"auto"}}>
        <table style={{width:"100%", borderCollapse:"collapse", fontSize:".88rem"}}>
          <thead>
            <tr style={{background:"rgba(193,127,58,.08)"}}>
              <th style={{padding:"14px", textAlign:"left", color:"var(--gold)", fontWeight:700}}>Date</th>
              <th style={{padding:"14px", textAlign:"left", color:"var(--gold)", fontWeight:700}}>Name</th>
              <th style={{padding:"14px", textAlign:"left", color:"var(--gold)", fontWeight:700}}>Email</th>
              <th style={{padding:"14px", textAlign:"left", color:"var(--gold)", fontWeight:700}}>Phone</th>
              <th style={{padding:"14px", textAlign:"left", color:"var(--gold)", fontWeight:700}}>State</th>
              <th style={{padding:"14px", textAlign:"left", color:"var(--gold)", fontWeight:700}}>Program</th>
              <th style={{padding:"14px", textAlign:"left", color:"var(--gold)", fontWeight:700}}>Status</th>
              <th style={{padding:"14px", textAlign:"left", color:"var(--gold)", fontWeight:700}}>Webhook</th>
              <th style={{padding:"14px", textAlign:"left", color:"var(--gold)", fontWeight:700}}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {leads.rows.map(r => (
              <tr key={r.id} style={{borderTop:"1px solid rgba(193,127,58,.10)"}}>
                <td style={{padding:"12px 14px", whiteSpace:"nowrap"}}>{(r.submitted_at||"").slice(0,16).replace("T"," ")}</td>
                <td style={{padding:"12px 14px"}}>{r.first_name} {r.last_name}</td>
                <td style={{padding:"12px 14px"}}>{r.email}</td>
                <td style={{padding:"12px 14px"}}>{r.phone || "—"}</td>
                <td style={{padding:"12px 14px"}}>{r.state}</td>
                <td style={{padding:"12px 14px"}}>{r.program_interest}</td>
                <td style={{padding:"12px 14px"}}>
                  <select value={r.status} onChange={(e)=>setStatus(r.id, e.target.value)} className="aa-select"
                          style={{padding:"6px 10px", fontSize:".8rem"}}>
                    <option value="New">New</option>
                    <option value="Contacted">Contacted</option>
                    <option value="Converted">Converted</option>
                  </select>
                </td>
                <td style={{padding:"12px 14px"}}>
                  <span style={{
                    background: r.webhook_status==="sent" ? "rgba(122,184,113,.22)" : "rgba(255,142,142,.22)",
                    color: r.webhook_status==="sent" ? "#7AB871" : "#ff8e8e",
                    padding:"4px 10px", borderRadius:12, fontSize:".75rem"
                  }}>
                    {r.webhook_status || "pending"}
                  </span>
                </td>
                <td style={{padding:"12px 14px"}}>
                  <button onClick={()=>resend(r.id)} disabled={resending===r.id} style={{
                    background:"transparent", border:"1px solid var(--gold)", color:"var(--gold)",
                    padding:"6px 12px", fontSize:".75rem", cursor:"pointer", borderRadius:4
                  }}>
                    {resending===r.id ? "Sending..." : "Resend"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{display:"flex", justifyContent:"space-between", marginTop:16}}>
        <div style={{color:"var(--muted)"}}>Page {page} of {totalPages}</div>
        <div style={{display:"flex", gap:8}}>
          <button disabled={page<=1} onClick={()=>setPage(p=>p-1)} style={{
            background:"transparent", border:"1px solid var(--gold)", color:"var(--gold)",
            padding:"8px 16px", cursor:"pointer", borderRadius:4
          }}>← Prev</button>
          <button disabled={page>=totalPages} onClick={()=>setPage(p=>p+1)} style={{
            background:"transparent", border:"1px solid var(--gold)", color:"var(--gold)",
            padding:"8px 16px", cursor:"pointer", borderRadius:4
          }}>Next →</button>
        </div>
      </div>
    </div>
  );
}

// Orders Table Component
function OrdersTable({ orders, page, setPage, pageSize }) {
  const totalPages = Math.max(1, Math.ceil(orders.total / pageSize));

  return (
    <div>
      <h2 className="aa-h2" style={{fontSize:"1.4rem", marginBottom:20}}>Orders ({orders.total})</h2>
      <div style={{background:"var(--dark)", border:"1px solid rgba(193,127,58,.22)", borderRadius:14, overflow:"auto"}}>
        <table style={{width:"100%", borderCollapse:"collapse", fontSize:".88rem"}}>
          <thead>
            <tr style={{background:"rgba(193,127,58,.08)"}}>
              <th style={{padding:"14px", textAlign:"left", color:"var(--gold)", fontWeight:700}}>Order ID</th>
              <th style={{padding:"14px", textAlign:"left", color:"var(--gold)", fontWeight:700}}>Date</th>
              <th style={{padding:"14px", textAlign:"left", color:"var(--gold)", fontWeight:700}}>Customer</th>
              <th style={{padding:"14px", textAlign:"left", color:"var(--gold)", fontWeight:700}}>Email</th>
              <th style={{padding:"14px", textAlign:"left", color:"var(--gold)", fontWeight:700}}>Items</th>
              <th style={{padding:"14px", textAlign:"left", color:"var(--gold)", fontWeight:700}}>Total</th>
              <th style={{padding:"14px", textAlign:"left", color:"var(--gold)", fontWeight:700}}>Payment</th>
              <th style={{padding:"14px", textAlign:"left", color:"var(--gold)", fontWeight:700}}>Status</th>
              <th style={{padding:"14px", textAlign:"left", color:"var(--gold)", fontWeight:700}}>Shipping</th>
            </tr>
          </thead>
          <tbody>
            {orders.rows && orders.rows.length === 0 && (
              <tr><td colSpan="9" style={{padding:"24px", textAlign:"center", color:"var(--muted)"}}>No orders yet</td></tr>
            )}
            {orders.rows && orders.rows.map(o => (
              <tr key={o.id} style={{borderTop:"1px solid rgba(193,127,58,.10)"}}>
                <td style={{padding:"12px 14px", fontFamily:"monospace", fontSize:".75rem"}}>{(o.id||"").slice(0,8)}</td>
                <td style={{padding:"12px 14px", whiteSpace:"nowrap"}}>{(o.created_at||"").slice(0,16).replace("T"," ")}</td>
                <td style={{padding:"12px 14px"}}>{o.customer_name}</td>
                <td style={{padding:"12px 14px"}}>{o.customer_email}</td>
                <td style={{padding:"12px 14px"}}>{o.items ? o.items.length : 0} items</td>
                <td style={{padding:"12px 14px", color:"var(--gold)", fontWeight:700}}>${(o.total_cents/100).toFixed(2)}</td>
                <td style={{padding:"12px 14px", fontSize:".8rem"}}>Card •••• {o.card_last4 || "XXXX"}</td>
                <td style={{padding:"12px 14px"}}>
                  <span style={{
                    background: o.status==="completed" ? "rgba(122,184,113,.22)" : "rgba(255,142,142,.22)",
                    color: o.status==="completed" ? "#7AB871" : "#ff8e8e",
                    padding:"4px 10px", borderRadius:12, fontSize:".75rem", textTransform:"capitalize"
                  }}>
                    {o.status}
                  </span>
                </td>
                <td style={{padding:"12px 14px", maxWidth:200, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}} title={o.shipping_address}>
                  {o.shipping_address || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{display:"flex", justifyContent:"space-between", marginTop:16}}>
        <div style={{color:"var(--muted)"}}>Page {page} of {totalPages}</div>
        <div style={{display:"flex", gap:8}}>
          <button disabled={page<=1} onClick={()=>setPage(p=>p-1)} style={{
            background:"transparent", border:"1px solid var(--gold)", color:"var(--gold)",
            padding:"8px 16px", cursor:"pointer", borderRadius:4
          }}>← Prev</button>
          <button disabled={page>=totalPages} onClick={()=>setPage(p=>p+1)} style={{
            background:"transparent", border:"1px solid var(--gold)", color:"var(--gold)",
            padding:"8px 16px", cursor:"pointer", borderRadius:4
          }}>Next →</button>
        </div>
      </div>
    </div>
  );
}

// Webhooks Table Component
function WebhooksTable({ logs }) {
  return (
    <div>
      <h2 className="aa-h2" style={{fontSize:"1.4rem", marginBottom:20}}>Webhook Logs (Last 50)</h2>
      <div style={{background:"var(--dark)", border:"1px solid rgba(193,127,58,.22)", borderRadius:14, overflow:"auto"}}>
        <table style={{width:"100%", borderCollapse:"collapse", fontSize:".85rem"}}>
          <thead>
            <tr style={{background:"rgba(193,127,58,.08)"}}>
              <th style={{padding:"12px 14px", textAlign:"left", color:"var(--gold)", fontWeight:700}}>Timestamp</th>
              <th style={{padding:"12px 14px", textAlign:"left", color:"var(--gold)", fontWeight:700}}>Lead ID</th>
              <th style={{padding:"12px 14px", textAlign:"left", color:"var(--gold)", fontWeight:700}}>Endpoint</th>
              <th style={{padding:"12px 14px", textAlign:"left", color:"var(--gold)", fontWeight:700}}>Attempt</th>
              <th style={{padding:"12px 14px", textAlign:"left", color:"var(--gold)", fontWeight:700}}>Code</th>
              <th style={{padding:"12px 14px", textAlign:"left", color:"var(--gold)", fontWeight:700}}>Status</th>
              <th style={{padding:"12px 14px", textAlign:"left", color:"var(--gold)", fontWeight:700}}>Response</th>
            </tr>
          </thead>
          <tbody>
            {logs.length === 0 && (
              <tr><td colSpan="7" style={{padding:"24px", textAlign:"center", color:"var(--muted)"}}>No webhook events yet</td></tr>
            )}
            {logs.map(l => (
              <tr key={l.id} style={{borderTop:"1px solid rgba(193,127,58,.10)"}}>
                <td style={{padding:"10px 14px", whiteSpace:"nowrap"}}>{(l.timestamp||"").slice(0,19).replace("T"," ")}</td>
                <td style={{padding:"10px 14px", fontFamily:"monospace", fontSize:".7rem"}}>{(l.lead_id||"").slice(0,8)}</td>
                <td style={{padding:"10px 14px", maxWidth:200, overflow:"hidden", textOverflow:"ellipsis"}} title={l.endpoint}>{l.endpoint}</td>
                <td style={{padding:"10px 14px"}}>{l.attempt_number}</td>
                <td style={{padding:"10px 14px"}}>{l.response_code || "—"}</td>
                <td style={{padding:"10px 14px"}}>
                  <span style={{
                    color: l.status==="success" ? "#7AB871" : l.status==="skipped" ? "#8A9E84" : "#ff8e8e",
                    fontWeight:600
                  }}>
                    {l.status}
                  </span>
                </td>
                <td style={{padding:"10px 14px", maxWidth:250, overflow:"hidden", textOverflow:"ellipsis"}} title={l.response_body}>
                  {l.response_body}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Analytics Config Component
function AnalyticsConfig({ config, setConfig, onSave, saving, saveMessage }) {
  return (
    <div>
      <h2 className="aa-h2" style={{fontSize:"1.4rem", marginBottom:8}}>Analytics & Tracking Codes</h2>
      <p style={{color:"var(--muted)", marginBottom:24, fontSize:".9rem"}}>
        Add your tracking codes here. Changes apply immediately without code deployment.
      </p>

      {saveMessage && (
        <div style={{
          padding:"14px 18px",
          background: saveMessage.startsWith("Saved") ? "rgba(122,184,113,.15)" : "rgba(255,142,142,.15)",
          color: saveMessage.startsWith("Saved") ? "#7AB871" : "#ff8e8e",
          borderRadius:8,
          marginBottom:20,
          fontSize:".9rem"
        }}>
          {saveMessage}
        </div>
      )}

      <div style={{display:"flex", flexDirection:"column", gap:24}}>
        {/* Google Analytics */}
        <div style={{background:"var(--dark)", border:"1px solid rgba(193,127,58,.22)", borderRadius:14, padding:"24px"}}>
          <h3 className="aa-eyebrow" style={{marginBottom:12}}>Google Analytics (GA4)</h3>
          <p style={{color:"var(--muted)", fontSize:".85rem", marginBottom:12}}>
            Enter your Google Analytics Measurement ID (e.g., G-XXXXXXXXXX)
          </p>
          <input
            className="aa-input"
            placeholder="G-XXXXXXXXXX"
            value={config.google_analytics}
            onChange={(e)=>setConfig({...config, google_analytics:e.target.value})}
          />
        </div>

        {/* Facebook Pixel */}
        <div style={{background:"var(--dark)", border:"1px solid rgba(193,127,58,.22)", borderRadius:14, padding:"24px"}}>
          <h3 className="aa-eyebrow" style={{marginBottom:12}}>Facebook Pixel</h3>
          <p style={{color:"var(--muted)", fontSize:".85rem", marginBottom:12}}>
            Enter your Facebook Pixel ID
          </p>
          <input
            className="aa-input"
            placeholder="123456789012345"
            value={config.facebook_pixel}
            onChange={(e)=>setConfig({...config, facebook_pixel:e.target.value})}
          />
        </div>

        {/* TikTok Pixel */}
        <div style={{background:"var(--dark)", border:"1px solid rgba(193,127,58,.22)", borderRadius:14, padding:"24px"}}>
          <h3 className="aa-eyebrow" style={{marginBottom:12}}>TikTok Pixel</h3>
          <p style={{color:"var(--muted)", fontSize:".85rem", marginBottom:12}}>
            Enter your TikTok Pixel ID
          </p>
          <input
            className="aa-input"
            placeholder="ABCDEFGHIJK12345"
            value={config.tiktok_pixel}
            onChange={(e)=>setConfig({...config, tiktok_pixel:e.target.value})}
          />
        </div>

        {/* Custom Header */}
        <div style={{background:"var(--dark)", border:"1px solid rgba(193,127,58,.22)", borderRadius:14, padding:"24px"}}>
          <h3 className="aa-eyebrow" style={{marginBottom:12}}>Custom Header Code</h3>
          <p style={{color:"var(--muted)", fontSize:".85rem", marginBottom:12}}>
            Any custom scripts or code to inject in {"<head>"} (e.g., other tracking pixels, meta tags)
          </p>
          <textarea
            className="aa-textarea"
            rows={6}
            placeholder="<script>...</script>"
            value={config.custom_header}
            onChange={(e)=>setConfig({...config, custom_header:e.target.value})}
          />
        </div>

        {/* Custom Footer */}
        <div style={{background:"var(--dark)", border:"1px solid rgba(193,127,58,.22)", borderRadius:14, padding:"24px"}}>
          <h3 className="aa-eyebrow" style={{marginBottom:12}}>Custom Footer Code</h3>
          <p style={{color:"var(--muted)", fontSize:".85rem", marginBottom:12}}>
            Any custom scripts to inject before closing {"</body>"} tag
          </p>
          <textarea
            className="aa-textarea"
            rows={6}
            placeholder="<script>...</script>"
            value={config.custom_footer}
            onChange={(e)=>setConfig({...config, custom_footer:e.target.value})}
          />
        </div>

        {/* Save Button */}
        <button
          onClick={onSave}
          disabled={saving}
          className="aa-btn"
          style={{alignSelf:"flex-start", minWidth:200}}
        >
          {saving ? "Saving..." : "💾 Save Analytics Codes"}
        </button>
      </div>
    </div>
  );
}
